import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Copy, Share2, Link2 } from "lucide-react";
import { Screen, ScrollArea, BottomBar, PrimaryButton } from "@/components/jk/ui";
import { Card, Pill, SectionLabel } from "@/components/jk/kit";
import { useAuth } from "@/context/AuthContext";

const OnboardingComplete = () => {
  const navigate = useNavigate();
  const { astrologer } = useAuth();
  const [copied, setCopied] = useState(false);

  const slug = astrologer?.booking_slug || "";
  const link = `${window.location.origin}/${slug}`;
  const display = link.replace(/^https?:\/\//, "");

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch {}
  };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Book a consultation", url: link }); } catch {} }
    else copy();
  };

  const checklist = [
    { label: "Profile created", done: !!astrologer?.full_name },
    { label: "Services added", done: true },
    { label: "Availability set", done: true },
    { label: "Payout details", done: !!(astrologer?.payout_upi_id || astrologer?.payout_account_number) },
  ];

  return (
    <Screen testid="onboarding-complete-screen">
      <ScrollArea className="px-6 pt-12">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#111111]" data-testid="success-check">
            <Check size={38} strokeWidth={2.5} className="text-white" />
          </div>
          <h1 className="mt-6 text-[30px] font-bold leading-tight tracking-[-0.02em] text-[#111111]">You&apos;re all set!</h1>
          <p className="mt-2 text-[14px] leading-relaxed text-[#666666]">
            Your practice is ready. Share your booking link and start taking consultations.
          </p>
        </div>

        {/* Booking link */}
        <Card dark className="mt-8">
          <SectionLabel className="text-white/60" icon={<Link2 size={13} strokeWidth={2} />}>Your booking link</SectionLabel>
          <p className="mt-2 break-all text-[15px] font-semibold" data-testid="booking-link">{display}</p>
          <div className="mt-4 flex gap-3">
            <button onClick={copy} data-testid="copy-link" className="flex flex-1 items-center justify-center gap-2 rounded-[12px] bg-white py-3 text-[13px] font-semibold text-[#111111] active:scale-[0.98]">
              <Copy size={15} strokeWidth={2} /> {copied ? "Copied!" : "Copy"}
            </button>
            <button onClick={share} data-testid="share-link" className="flex flex-1 items-center justify-center gap-2 rounded-[12px] border border-white/30 py-3 text-[13px] font-semibold text-white active:scale-[0.98]">
              <Share2 size={15} strokeWidth={2} /> Share
            </button>
          </div>
        </Card>

        {/* Account status */}
        <Card className="mt-4">
          <div className="flex items-center justify-between">
            <SectionLabel>Account status</SectionLabel>
            <Pill tone="periwinkle">Free plan</Pill>
          </div>
          <p className="mt-3 text-[13px] text-[#666666]">0 of 5 free bookings used</p>
          <div className="mt-2 flex gap-1.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-1.5 flex-1 rounded-full bg-[#ece9e4]" />
            ))}
          </div>
          <div className="mt-4 space-y-2.5">
            {checklist.map((c) => (
              <div key={c.label} className="flex items-center gap-2.5">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full ${c.done ? "bg-[#111111]" : "border border-[#d8d4ce]"}`}>
                  {c.done && <Check size={12} strokeWidth={3} className="text-white" />}
                </span>
                <span className={`text-[13px] ${c.done ? "text-[#111111]" : "text-[#aaaaaa]"}`}>{c.label}</span>
              </div>
            ))}
          </div>
        </Card>
        <div className="h-6" />
      </ScrollArea>

      <BottomBar>
        <PrimaryButton testid="go-to-dashboard" onClick={() => navigate("/dashboard", { replace: true })}>
          Go to Dashboard
        </PrimaryButton>
      </BottomBar>
    </Screen>
  );
};

export default OnboardingComplete;
